import { useState, useEffect } from "react";
import Button from "../../../../../button/button"
import InputAnimated from "../../../../../inputAnimated/inputAnimated"
import PaySVG from "../../../../../icons/pay";
import CardSVG from "../../../../../icons/card";
import BankSVG from "../../../../../icons/bank";
import './PaymentMethod.scss';
import Tooltip from "../../../../../Tooltip/Tooltip";

const PaymentMethod = (props) => {

    // States for bank transfer details
    const [trackingKey, setTrackingKey] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [bankName, setBankName] = useState('');
    const [accountHolderName, setAccountHolderName] = useState('');

    // Options for the payment method select
    const paymentMethod = [
        { value: 'Efectivo', label: 'Efectivo' },
        { value: 'Débito', label: 'Débito' },
        { value: 'Crédito', label: 'Crédito' },
        { value: 'Transferencia', label: 'Transferencia' },
    ];

    // States for the selected payment method and currency (MXN by default)
    const [paymentMethodValue, setPaymentMethodValue] = useState('Efectivo');
    const [selectedCurrency, setSelectedCurrency] = useState('MXN'); // 'MXN' or 'USD'
    const [paymentMethodSelected, setPaymentMethodSelected] = useState([
        {
            method: '',
            amount: '',
            currency: ''
        }
    ]);
    // State for the count of bills entered
    const [billCounts, setBillCounts] = useState({});

    // Arrays of denominations for MXN and USD
    const billsMXN = [
        { denomination: 1000, label: '$ 1000' },
        { denomination: 500, label: '$ 500' },
        { denomination: 200, label: '$ 200' },
        { denomination: 100, label: '$ 100' },
        { denomination: 50, label: '$ 50' },
    ];

    const billsUSD = [
        { denomination: 100, label: '$ 100' },
        { denomination: 50, label: '$ 50' },
        { denomination: 20, label: '$ 20' },
        { denomination: 10, label: '$ 10' },
        { denomination: 5, label: '$ 5' },
        { denomination: 1, label: '$ 1' },
    ];

    // When the currency changes, reset the bill counts to 0
    useEffect(() => {
        const bills = selectedCurrency === 'MXN' ? billsMXN : billsUSD;
        const initialCounts = {};
        bills.forEach(bill => {
            initialCounts[bill.denomination] = 0;
        });
        setBillCounts(initialCounts);
        // When the currency changes, reset the manual edit flag
        setUserEditedReceived(false);
    }, [selectedCurrency]);

    // Automatic calculation of cash received based on bills
    const computedTotalReceived = Object.keys(billCounts).reduce((total, key) => {
        return total + (Number(key) * billCounts[key]);
    }, 0);

    // States for editable cash received and flag to detect manual editing
    const [manualReceived, setManualReceived] = useState(computedTotalReceived);
    const [userEditedReceived, setUserEditedReceived] = useState(false);

    // If the user hasn't manually edited, update the cash received automatically
    useEffect(() => {
        if (!userEditedReceived) {
            setManualReceived(computedTotalReceived);
        }
    }, [computedTotalReceived, userEditedReceived]);

    // Calculate the change (always in MXN)
    // If the currency is USD, convert the cash received to MXN using props.exchange
    const changeValue =
        selectedCurrency === 'MXN'
            ? manualReceived - props.totalAmount
            : (manualReceived * props.exchange) - props.totalAmount;
    const finalChange = changeValue > 0 ? changeValue.toFixed(2) : "0.00";

    const updateMethodSelected = (newMethod, newAmount, newCurrency) => {
        let finalAmount = newAmount;

        // Si el método es 'Efectivo' y la moneda es 'USD', convertimos el monto usando el tipo de cambio.
        if (newMethod === 'Efectivo' && newCurrency === 'USD') {
            finalAmount = (newAmount / props.exchange).toFixed(2); // Convertir de MXN a USD
        } else if (newMethod != 'Efectivo') {
            newCurrency = 'MXN'
        }

        const newPayment = {
            method: newMethod,
            amount: finalAmount,
            currency: newCurrency,
        };

        if (newMethod === 'Transferencia') {
            newPayment.account_holder_name = accountHolderName;
            newPayment.tracking_key = trackingKey;
            newPayment.account_number = accountNumber;
            newPayment.bank_name = bankName;
        }

        setPaymentMethodSelected([newPayment]);
    };
    useEffect(() => {
        updateMethodSelected(paymentMethodValue, props.totalAmount, selectedCurrency);
    }, [props.totalAmount, 
        accountHolderName,
        trackingKey,
        accountNumber,
        bankName])

    useEffect(() => {
        props.setPaymentMethod(paymentMethodSelected)
    }, [paymentMethodSelected]); // Se activará cuando `paymentMethodSelected` cambie.

    const handlePaymentMethodChange = (value) => {
        setPaymentMethodValue(value);
        // Actualizar el objeto en paymentMethodSelected
        updateMethodSelected(value, props.totalAmount, selectedCurrency);

    };

    // Function to select the icon corresponding to the payment method
    const selectIcon = (button) => {
        switch (button) {
            case 'Efectivo': return PaySVG;
            case 'Débito': return CardSVG;
            case 'Crédito': return CardSVG;
            case 'Transferencia': return BankSVG;
            default: return null;
        }
    };

    // When the user manually edits the field "Efectivo recibido"
    const handleManualReceivedChange = (e) => {
        const newValue = Number(e.target.value);
        setManualReceived(newValue);
        setUserEditedReceived(true);

        // Reset bills to 0 when editing manually
        const bills = selectedCurrency === 'MXN' ? billsMXN : billsUSD;
        const initialCounts = {};
        bills.forEach(bill => {
            initialCounts[bill.denomination] = 0;
        });
        setBillCounts(initialCounts);
    };

    // When a bill is increased or decreased
    const updateBillCount = (denom, increment) => {
        setBillCounts(prev => {
            const newCounts = {
                ...prev,
                [denom]: increment ? prev[denom] + 1 : (prev[denom] > 0 ? prev[denom] - 1 : 0)
            };

            // Calculate the new amount received based in bills
            const newTotalReceived = Object.keys(newCounts).reduce((total, key) => {
                return total + (Number(key) * newCounts[key]);
            }, 0);

            // Actualizar el campo de "Efectivo recibido" para que coincida con los billetes
            setManualReceived(newTotalReceived);
            setUserEditedReceived(false); // Resetear el flag de edición manual

            return newCounts;
        });
    };

    const handleCurrencyChange = (currency) => {
        setSelectedCurrency(currency);
        // Actualizar con la moneda recién seleccionada
        updateMethodSelected(paymentMethodValue, props.totalAmount, currency);

    };
    // Functions to increase and decrease the bills
    const incrementBill = (denom) => {
        updateBillCount(denom, true);
    };
    const decrementBill = (denom) => {
        updateBillCount(denom, false);
    };

    return (
        <div className='payment-method'>
            <h1 className="title-3 first-title">Método de pago</h1>
            <div className="payment-method-buttons">
                {paymentMethod.map((method) => (
                    <Button
                        key={method.value}
                        text={method.label}
                        backgroundColor={paymentMethodValue === method.value ? "blue" : "gray"}
                        onClick={() => handlePaymentMethodChange(method.value)}
                        Icon={selectIcon(method.value)}
                    />
                ))}
            </div>

            {/* If the payment method is 'Efectivo', show additional fields */}
            {paymentMethodValue === 'Efectivo' && (
                <div className='change'>
                    {/* Buttons to select the currency */}
                    <div className="currency-buttons">
                        <Button
                            text="MXN"
                            backgroundColor={selectedCurrency === 'MXN' ? "blue" : "gray"}
                            onClick={() => handleCurrencyChange('MXN')}

                        />
                        <Button
                            text="USD"
                            backgroundColor={selectedCurrency === 'USD' ? "blue" : "gray"}
                            onClick={() => handleCurrencyChange('USD')}
                        />
                    </div>

                    {/* Editable input for cash received */}
                    <Tooltip text={'Ingresar Efectivo recivido para calcular el cambio'}>
                        <InputAnimated
                            placeholder={'Efectivo recibido'}
                            type="number"
                            value={manualReceived}
                            onChange={handleManualReceivedChange}
                        />
                    </Tooltip>
                    <div className='bills-change'>
                        {(selectedCurrency === 'MXN' ? billsMXN : billsUSD).map((bill, index) => (
                            <div key={index} className={`bill ${selectedCurrency}-${bill.denomination}`}>
                                <h1 className="bill-name">{bill.label}</h1>
                                <div className="bill-controls">
                                    <button onClick={() => decrementBill(bill.denomination)}>-</button>
                                    <span>{billCounts[bill.denomination]}</span>
                                    <button onClick={() => incrementBill(bill.denomination)}>+</button>
                                </div>
                            </div>
                        ))}
                    </div>
                    <h1 className='title-2'>Cambio: ${finalChange} MXN</h1>
                </div>
            )}
            {(paymentMethodValue === 'Débito' || paymentMethodValue === 'Crédito') && (
                <div className='card-payment-txt'>
                    Completa la transacción con la terminal bancaria, posteriormente finaliza el pago con el boton inferior.
                </div>
            )}
            {/* If the payment method is 'Transferencia', show additional fields */}
            {paymentMethodValue === 'Transferencia' && (
                <div className='transfer-details'>
                    <h1 className="title-2">Datos de transferencia</h1>
                    <InputAnimated
                        placeholder={'Nombre del titular'}
                        value={accountHolderName}
                        onChange={(e) => setAccountHolderName(e.target.value)}
                    />
                    <InputAnimated
                        placeholder={'clave de rastreo'}
                        value={trackingKey}
                        onChange={(e) => setTrackingKey(e.target.value)}
                    />
                    <InputAnimated
                        placeholder={'Cuenta emisora'}
                        value={accountNumber}
                        onChange={(e) => setAccountNumber(e.target.value)}
                    />
                    <InputAnimated
                        placeholder={'Banco emisor'}
                        value={bankName}
                        onChange={(e) => setBankName(e.target.value)}
                    />
                </div>
            )}
        </div>

    )
}

export default PaymentMethod